import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  catchError,
  concatMap,
  EMPTY,
  finalize,
  forkJoin,
  Observable,
  of,
  retry,
  Subscription,
  switchMap,
  tap,
  throwError,
  timer,
} from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { SpacesDispensesHD } from 'src/app/shared/interfaces/IHDspace';
import { IActions, IScheduleActionParams } from 'src/app/shared/interfaces/IScheduleActionParams';
import { ApiErrorHandlerService } from 'src/app/shared/services/api-error-handler/api-error-handler.service';
import { TSettings } from 'src/app/shared/services/appconfig/settings.model';
import { BabelfishService } from 'src/app/shared/services/babelfish/babelfish.service';
import { DeviceService } from 'src/app/shared/services/device/device.service';
import { HelmsdeepService } from 'src/app/shared/services/helmsdeep/helmsdeep.service';
import { selectSettings } from 'src/app/stores/settings';

import { LockerActionDataService } from '../../../services/locker-action-data/locker-action-data.service';
import {
  APP_ID,
  DISPENSE_ACTIONS,
  DISPENSE_STATES,
  SIDES,
  SPACE_ACTIONS,
  SPACE_STATES,
  TOAST_TYPES,
  VIEW_CALLING,
  VIEW_TYPES,
} from '../../../utils/constants';

const UNFILL_RETRY_DELAY_MS = 1000;

@Component({
  selector: 'frodo-confirmation',
  templateUrl: './confirmation.component.html',
  styleUrls: ['./confirmation.component.scss'],
})
export class ConfirmationComponent implements OnInit, OnDestroy {
  flow: string;
  logo: string;
  homeButton: string;
  helpButton: string;
  viewCalling = '';
  title = '';
  scheduleId: string;
  spaceId: string;
  spaceSelectableState: string;
  stateSelected: string;
  scheduleName: string;
  deliveryType: any;
  dispensesToReclaim;
  isLoading = false;
  positionLabel: string;
  state: RouterStateSnapshot;
  nextUrl: string;
  returnUrl: string;
  viewType: string;
  spaceStateToChange: string;
  dispenseId: string;
  spaceState: string;
  deviceSn: string;
  inactivityTimer: any;
  hdSettings$: Observable<Partial<TSettings>>;
  validatePresenceSensor = false;
  subscription: Subscription = new Subscription();
  positions: string[] = [];

  currentSpaceInfo: SpacesDispensesHD;
  private currentDispenseInfo;

  private deliveryOrderConfirmation = 'common.deliveryOrderConfirmation';
  private reclaimOrderConfirmation = 'common.reclaimOrderConfirmation';
  private reclaimRetailOrderConfirmation = 'common.reclaimRetailOrderConfirmation';
  private removeRetailOrderConfirmation = 'common.removeRetailOrderConfirmation';
  private lockdownRetailOrderConfirmation = 'common.lockdownRetailOrderConfirmation';
  private verifyRetailOrderConfirmation = 'common.verifyRetailOrderConfirmation';
  private dispenseRetailOrderConfirmation = 'common.dispenseRetailOrderConfirmation';
  private reclaimAllOrdersConfirmation = 'common.reclaimAllOrdersConfirmation';
  private openDoorRetailTitle = 'common.openDoorRetailTitle';
  private openDoorTitle = 'common.openDoorTitle';
  private openDoorSuccessMsg = 'common.openDoorSuccessMsg';
  private selectableSpaceTitle = 'common.selectableSpaceTitle';
  private spaceSelectableErrorMessage = 'common.spaceSelectableErrorMessage';
  private resetSpaceTitle = 'common.resetSpaceTitle';
  private resetSpaceRetailTitle = 'common.resetSpaceRetailTitle';
  private disableSpaceRetailTitle = 'common.disableSpaceRetailTitle';
  private enableSpaceRetailTitle = 'common.enableSpaceRetailTitle';
  private orderReclaimedSuccessfulMessage = 'common.orderReclaimedSuccessfulMessage';
  private orderDispensedSuccessfulMessage = 'common.orderDispensedSuccessfulMessage';
  private orderVerifiedSuccessfulMessage = 'common.orderVerifiedSuccessfulMessage';
  private orderLockedSuccessfulMessage = 'common.orderLockedSuccessfulMessage';
  private orderErrorMessage = 'common.orderErrorMessage';
  private spaceDisableSuccessfulMessage = 'common.spaceDisableSuccessfulMessage';
  private spaceEnableSuccessfulMessage = 'common.spaceEnableSuccessfulMessage';
  private spaceResetSuccessfulMessage = 'common.spaceResetSuccessfulMessage';
  private spaceResetErrorMessage = 'common.spaceResetErrorMessage';
  private confirmReclaimAllButtonText = 'common.confirmReclaimAllButtonText';
  private confirmReclaimButtonText = 'common.confirmReclaimButtonText';
  private confirmRemoveButtonText = 'common.confirmRemoveButtonText';
  private confirmDispenseButtonText = 'common.confirmDispenseButtonText';
  private confirmLockdownButtonText = 'common.confirmLockdownButtonText';
  private confirmVerifyButtonText = 'common.confirmVerifyButtonText';
  private confirmResetButtonText = 'common.confirmResetButtonText';
  private confirmDisableButtonText = 'common.confirmDisableButtonText';
  private confirmEnableButtonText = 'common.confirmEnableButtonText';
  private confirmOpenButtonText = 'common.confirmOpenButtonText';
  private cancelRetailButtonText = 'common.cancelRetailButtonText';
  private confirmButtonText = 'common.yes';
  private cancelButtonText = 'common.no';
  private errorMessage = 'common.errorMessage';
  private reclaimAllErrorMessage = 'common.reclaimAllErrorMessage';
  private reclaimAllSuccessfulMessage = 'common.reclaimAllSuccessfulMessage';
  private removeLightsTimeoutRef: NodeJS.Timeout;
  private removeLightsTimeout: number;
  confirmButton: string;
  cancelButton: string;

  constructor(
    private router: Router,
    private store: Store,
    private activatedRoute: ActivatedRoute,
    private helmsdeepService: HelmsdeepService,
    private apiErrorHandlerService: ApiErrorHandlerService,
    private babelfishService: BabelfishService,
    private deviceService: DeviceService,
    private translateService: TranslateService,
    private lockerActionDataService: LockerActionDataService
  ) {
    this.viewCalling = this.activatedRoute.snapshot.paramMap.get('viewCalling');
    this.deliveryType = this.activatedRoute.snapshot.paramMap.get('deliveryType');
    this.flow = this.activatedRoute.snapshot.paramMap.get('flow');
    this.dispensesToReclaim = this.activatedRoute.snapshot.paramMap.get('dispensesToReclaim');
    this.spaceSelectableState = this.activatedRoute.snapshot.paramMap.get('spaceSelectableState');
    this.returnUrl = this.activatedRoute.snapshot.paramMap.get('returnUrl');

    this.state = router.routerState.snapshot;
    this.subscription.add(
      this.deviceService.getSerialNum.subscribe((serialNum) => {
        this.deviceSn = serialNum;
      })
    );
  }

  ngOnInit(): void {
    this.subscription.add(
      this.store.select(selectSettings).subscribe((settings) => {
        this.validatePresenceSensor = settings.device.validatePresenceSensor;
        const frodoSettings = settings.apps.frodo;
        this.logo = frodoSettings.style.logo;
        this.helpButton = frodoSettings.style.icons.helpButton;
        this.homeButton = frodoSettings.style.icons.homeButton;
        this.removeLightsTimeout = frodoSettings.timeouts.removeLightsTimeout;
        this.inactivityTimeout(frodoSettings.timeouts.cancelRemoveTimeout);
      })
    );
    this.viewType = this.state.url.split('/')[1];

    this.setValues();

    this.setTitleAndState();
    this.setNextUrl();
    this.setButtonTitles();
  }

  setValues() {
    this.currentSpaceInfo = this.lockerActionDataService.selectSpaceInfo;
    if (!this.currentSpaceInfo || Object.keys(this.currentSpaceInfo).length === 0) {
      this.setValuesFromRoute();
      return;
    }

    this.spaceId = this.currentSpaceInfo.id;
    this.positionLabel = this.currentSpaceInfo.hardwareInfo.position;
    this.spaceState = JSON.stringify(this.currentSpaceInfo.state);

    if (this.currentSpaceInfo?.dispenses.length) {
      this.currentDispenseInfo = this.currentSpaceInfo.dispenses[0];
      this.scheduleName = this.currentDispenseInfo.scheduleInfo.clientCode;
      this.scheduleId = this.currentDispenseInfo.schedule;
      this.stateSelected = this.currentDispenseInfo.state;
      this.dispenseId = this.currentDispenseInfo.dispense;
    }
  }

  /**
   * The order can also be identified by a code instead of by tapping a space, in which case there
   * is no selected space: the schedule comes in the route.
   */
  private setValuesFromRoute(): void {
    const params = this.activatedRoute.snapshot.paramMap;
    this.scheduleId = params.get('scheduleId');
    this.scheduleName = params.get('scheduleName');
    this.stateSelected = params.get('stateSelected');
  }

  setTitleAndState(): void {
    const getTranslation = (key: string, params: any) => this.translateService.instant(key, params);
    const getTitleForRetail = (key: string) => getTranslation(key, { orderId: this.scheduleName });

    const titleMap = {
      [VIEW_CALLING.LOADING_ORDER]: () => {
        this.title = this.deliveryOrderConfirmation;
      },
      [VIEW_CALLING.RECLAIM_ORDER]: () => {
        this.title =
          this.viewType === VIEW_TYPES.RETAIL
            ? getTranslation(this.reclaimRetailOrderConfirmation, {
                orderId: this.scheduleName,
              })
            : getTranslation(this.reclaimOrderConfirmation, {
                orderId: this.scheduleName,
              });
      },
      [VIEW_CALLING.REMOVE_ITEM]: () => {
        this.title =
          this.viewType === VIEW_TYPES.RETAIL
            ? getTranslation(this.removeRetailOrderConfirmation, {
                orderId: this.scheduleName,
              })
            : getTranslation(this.reclaimOrderConfirmation, {
                orderId: this.scheduleName,
              });
      },
      [VIEW_CALLING.RECLAIM_ALL_ORDER]: () => {
        this.title = this.reclaimAllOrdersConfirmation;
      },
      [VIEW_CALLING.OPEN_DOOR]: () => {
        this.title =
          this.viewType === VIEW_TYPES.RETAIL
            ? getTranslation(this.openDoorRetailTitle, {
                label: this.positionLabel,
              })
            : this.openDoorTitle;
      },
      [VIEW_CALLING.SELECTABLE_SPACE]: () => {
        this.spaceStateToChange =
          this.spaceSelectableState === SPACE_STATES.DISABLE
            ? SPACE_ACTIONS.ENABLE
            : SPACE_ACTIONS.DISABLE;
        this.title = getTranslation(this.selectableSpaceTitle, {
          state: this.spaceStateToChange,
          label: this.positionLabel,
        });
      },
      [VIEW_CALLING.RESET_SPACE]: () => {
        this.title =
          this.viewType === VIEW_TYPES.RETAIL
            ? getTranslation(this.resetSpaceRetailTitle, {
                label: this.positionLabel,
              })
            : getTranslation(this.resetSpaceTitle, {
                label: this.positionLabel,
              });
      },
      [VIEW_CALLING.DISABLE_SPACE]: () => {
        this.spaceStateToChange = SPACE_ACTIONS.DISABLE;
        this.title = getTranslation(this.disableSpaceRetailTitle, {
          label: this.positionLabel,
        });
      },
      [VIEW_CALLING.ENABLE_SPACE]: () => {
        this.spaceStateToChange = SPACE_ACTIONS.ENABLE;
        this.title = getTranslation(this.enableSpaceRetailTitle, {
          label: this.positionLabel,
        });
      },
      [VIEW_CALLING.LOCKDOWN_ORDER]: () => {
        this.title = getTitleForRetail(this.lockdownRetailOrderConfirmation);
      },
      [VIEW_CALLING.VERIFY_ORDER]: () => {
        this.title = getTranslation(this.verifyRetailOrderConfirmation, {
          orderId: this.scheduleName,
        });
      },
      [VIEW_CALLING.DISPENSE_ORDER]: () => {
        this.title = getTitleForRetail(this.dispenseRetailOrderConfirmation);
      },
    };
    const setTitleAction = titleMap[this.viewCalling] || (() => {});
    setTitleAction();
  }

  setButtonTitles(): void {
    const retailCancelButtonText = this.cancelRetailButtonText;
    const buttonMappingRetail = {
      [VIEW_CALLING.RECLAIM_ALL_ORDER]: {
        confirm: this.confirmReclaimAllButtonText,
        cancel: retailCancelButtonText,
      },
      [VIEW_CALLING.RECLAIM_ORDER]: {
        confirm: this.confirmReclaimButtonText,
        cancel: retailCancelButtonText,
      },
      [VIEW_CALLING.REMOVE_ITEM]: {
        confirm: this.confirmRemoveButtonText,
        cancel: retailCancelButtonText,
      },
      [VIEW_CALLING.DISPENSE_ORDER]: {
        confirm: this.confirmDispenseButtonText,
        cancel: retailCancelButtonText,
      },
      [VIEW_CALLING.LOCKDOWN_ORDER]: {
        confirm: this.confirmLockdownButtonText,
        cancel: retailCancelButtonText,
      },
      [VIEW_CALLING.VERIFY_ORDER]: {
        confirm: this.confirmVerifyButtonText,
        cancel: retailCancelButtonText,
      },
      [VIEW_CALLING.RESET_SPACE]: {
        confirm: this.confirmResetButtonText,
        cancel: retailCancelButtonText,
      },
      [VIEW_CALLING.DISABLE_SPACE]: {
        confirm: this.confirmDisableButtonText,
        cancel: retailCancelButtonText,
      },
      [VIEW_CALLING.ENABLE_SPACE]: {
        confirm: this.confirmEnableButtonText,
        cancel: retailCancelButtonText,
      },
      [VIEW_CALLING.OPEN_DOOR]: {
        confirm: this.confirmOpenButtonText,
        cancel: retailCancelButtonText,
      },
      default: {
        confirm: this.confirmButtonText,
        cancel: retailCancelButtonText,
      },
    };

    if (this.viewType === VIEW_TYPES.RETAIL) {
      const { confirm, cancel } =
        buttonMappingRetail[this.viewCalling] || buttonMappingRetail.default;
      this.confirmButton = confirm;
      this.cancelButton = cancel;
    } else {
      this.confirmButton = this.confirmButtonText;
      this.cancelButton = this.cancelButtonText;
    }
  }

  setNextUrl(): void {
    if (this.returnUrl) {
      this.nextUrl = this.returnUrl;
      return;
    }

    const viewUrlMapping = {
      [VIEW_TYPES.FRONT_LOADING]: `${VIEW_TYPES.FRONT_LOADING}/reclaim`,
      [VIEW_TYPES.REAR_LOADING]: `${VIEW_TYPES.REAR_LOADING}/status`,
      [VIEW_TYPES.RETAIL]: `${VIEW_TYPES.RETAIL}/status`,
    };
    if (this.viewCalling === VIEW_CALLING.LOADING_ORDER) {
      this.nextUrl = `/${VIEW_TYPES.FRONT_LOADING}/delivery-options`;
    } else {
      this.nextUrl = viewUrlMapping[this.viewType] || '';
    }
  }

  clearInactivityTimerAndNavigate(): void {
    clearTimeout(this.inactivityTimer);
    this.router.navigate([`${this.nextUrl}`]);
  }

  handleConfirmOptionForLoadingOrder(): void {
    this.router.navigate([
      `/${VIEW_TYPES.FRONT_LOADING}/create-order`,
      { type: this.deliveryType, flow: this.flow },
    ]);
  }

  handleConfirmOptionForReclaimOrder(): void {
    this.helmsdeepService
      .getData('schedules', this.scheduleId, 'dispenses')
      .pipe(
        tap((scheduleNdispenses: any) => {
          const keepScheduleNdispenses = this.prepareScheduleData(scheduleNdispenses);
          const action =
            this.stateSelected === DISPENSE_STATES.LOADED
              ? DISPENSE_ACTIONS.DO_REMOVE
              : DISPENSE_ACTIONS.DO_RECLAIM;
          const side = this.viewCalling === VIEW_CALLING.RECLAIM_ORDER ? SIDES.FRONT : SIDES.REAR;

          this.startReclaim(keepScheduleNdispenses, action, side);
        }),
        catchError((error) => {
          this.apiErrorHandlerService.handleApiError(error, '');
          return of(null);
        })
      )
      .subscribe();
  }

  prepareScheduleData(scheduleNdispenses: any) {
    const keepScheduleNdispenses = {};
    scheduleNdispenses.dispenses.forEach((dispense) => {
      if (dispense.state == this.stateSelected) {
        if (!keepScheduleNdispenses[dispense.schedule]) {
          keepScheduleNdispenses[dispense.schedule] = {
            dispensesId: [],
            spacesId: [],
            spacesOids: [],
            positionsNoids: {},
          };
        }
        keepScheduleNdispenses[dispense.schedule].dispensesId.push(dispense.id);
        keepScheduleNdispenses[dispense.schedule].spacesId.push(dispense.space);
        keepScheduleNdispenses[dispense.schedule].spacesOids.push(dispense.spaceInfo.oid);
        keepScheduleNdispenses[dispense.schedule].positionsNoids[
          dispense.spaceInfo.hardwareInfo.position
        ] = dispense.spaceInfo.oid;
      }
    });
    return keepScheduleNdispenses;
  }

  handleConfirmOptionForReclaimAllOrder(): void {
    clearTimeout(this.inactivityTimer);
    this.subscription.add(
      this.helmsdeepService
        .getSpaces({ includeDispenses: true })
        .subscribe(async (spacesNdispenses: SpacesDispensesHD[]) => {
          const validData = this.filterValidData(spacesNdispenses);
          const { scheduleActions, spaceActions } = this.generateActions(validData);
          if (scheduleActions.length) {
            this.cleanData(scheduleActions, spaceActions);
          }
        })
    );
  }

  filterValidData(data: SpacesDispensesHD[]): SpacesDispensesHD[] {
    return data.filter(
      (item) =>
        item.dispenses &&
        item.dispenses.length > 0 &&
        item.dispenses.some((d) => d.state === DISPENSE_STATES.LOCKEDDOWN)
    );
  }

  generateActions(data: SpacesDispensesHD[]): {
    scheduleActions: IActions[];
    spaceActions: IActions[];
  } {
    const uniqueSchedules = new Set<string>();
    const uniqueSpaces = new Set<string>();

    const scheduleActions: IActions[] = [];
    const spaceActions: IActions[] = [];

    data.forEach((item) => {
      item.dispenses.forEach(({ schedule, space }) => {
        if (!uniqueSchedules.has(schedule)) {
          uniqueSchedules.add(schedule);
          scheduleActions.push(
            {
              resourceId: schedule,
              type: DISPENSE_ACTIONS.DO_TRANSACTION,
              params: { actionType: DISPENSE_ACTIONS.DO_RECLAIM },
            },
            {
              resourceId: schedule,
              type: DISPENSE_ACTIONS.DO_TRANSACTION,
              params: { actionType: DISPENSE_ACTIONS.FINISH_RECLAIM },
            }
          );
        }

        if (!uniqueSpaces.has(space)) {
          uniqueSpaces.add(space);
          spaceActions.push({
            resourceId: space,
            type: 'UNFILL',
            params: {},
          });
        }
      });
    });

    return { scheduleActions, spaceActions };
  }

  cleanData(scheduleActions: IActions[], spaceActions: IActions[]): void {
    this.subscription.add(
      this.helmsdeepService.actions(scheduleActions).subscribe(
        () => {
          if (this.validatePresenceSensor) {
            const message = this.translateService.instant(this.reclaimAllSuccessfulMessage);
            this.showToastMessage(message, TOAST_TYPES.Success);
          } else {
            this.subscription.add(
              this.helmsdeepService
                .actions(spaceActions)
                .pipe(
                  finalize(() => {
                    const message = this.translateService.instant(this.reclaimAllSuccessfulMessage);
                    this.showToastMessage(message, TOAST_TYPES.Success);
                  })
                )
                .subscribe({
                  error: () => {
                    const message = this.translateService.instant(this.reclaimAllErrorMessage);
                    this.showToastMessage(message, TOAST_TYPES.Error);
                  },
                })
            );
          }
        },
        () => {
          const message = this.translateService.instant(this.reclaimAllErrorMessage);
          this.showToastMessage(message, TOAST_TYPES.Error);
        }
      )
    );
  }

  handleConfirmOptionForResetSpace(): void {
    if (this.scheduleId) {
      this.helmsdeepService
        .deleteOrder(this.scheduleId)
        .pipe(
          switchMap(() => {
            if (this.nextUrl === `${VIEW_TYPES.FRONT_LOADING}/reclaim`) {
              return this.helmsdeepService.actions([
                {
                  resourceId: this.spaceId,
                  type: SPACE_ACTIONS.OPEN,
                  params: {},
                },
              ]);
            } else {
              return of(null);
            }
          }),
          tap(() => {
            const message = this.translateService.instant(this.spaceResetSuccessfulMessage, {
              label: this.positionLabel,
            });
            this.showToastMessage(message, TOAST_TYPES.Success);
          }),
          catchError(() => {
            const message = this.translateService.instant(this.spaceResetErrorMessage, {
              label: this.positionLabel,
            });
            this.showToastMessage(message, TOAST_TYPES.Error);
            return of(null);
          })
        )
        .subscribe();
    } else {
      this.resetSpaceState();
    }
  }

  resetSpaceState(): void {
    const currentState = JSON.parse(this.spaceState);
    if (
      !(
        currentState.selectable === SPACE_STATES.RESERVE ||
        currentState.presence === SPACE_STATES.FILL ||
        currentState.secure === SPACE_STATES.OPEN
      )
    ) {
      this.router.navigate([`${this.nextUrl}`]);
      return;
    }

    this.helmsdeepService
      .resetSpace(this.spaceId)
      .pipe(
        tap(() => {
          const message = this.translateService.instant(this.spaceResetSuccessfulMessage, {
            label: this.positionLabel,
          });
          this.showToastMessage(message, TOAST_TYPES.Success);
        }),
        catchError(() => {
          const message = this.translateService.instant(this.spaceResetErrorMessage, {
            label: this.positionLabel,
          });
          this.showToastMessage(message, TOAST_TYPES.Error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  handleConfirmOptionForSelectableSpace(): void {
    const stateToChange = [
      {
        resourceId: this.spaceId,
        type: this.spaceStateToChange,
        params: {},
      },
    ];

    this.helmsdeepService
      .actions(stateToChange)
      .pipe(
        tap(() => {
          this.spaceStateToChange =
            this.spaceStateToChange === SPACE_ACTIONS.DISABLE
              ? this.spaceDisableSuccessfulMessage
              : this.spaceEnableSuccessfulMessage;
          const message = this.translateService.instant(this.spaceStateToChange, {
            label: this.positionLabel,
          });
          this.showToastMessage(message, TOAST_TYPES.Success);
        }),
        catchError(() => {
          this.spaceStateToChange =
            this.spaceStateToChange === SPACE_ACTIONS.DISABLE
              ? SPACE_STATES.DISABLE
              : SPACE_STATES.ENABLE;
          const message = this.translateService.instant(this.spaceSelectableErrorMessage, {
            state: this.spaceStateToChange.toLowerCase(),
            label: this.positionLabel,
          });
          this.showToastMessage(message, TOAST_TYPES.Error);
          return EMPTY;
        }),
        finalize(() => {})
      )
      .subscribe();
  }

  handleConfirmOptionForOpenDoor(): void {
    const body = [{ type: SPACE_ACTIONS.OPEN, params: {} }];
    const endpoint = `spaces/${this.spaceId}/_actions`;

    this.helmsdeepService
      .postData(endpoint, body)
      .pipe(
        tap(() => {
          const message = this.translateService.instant(this.openDoorSuccessMsg, {
            label: this.positionLabel,
          });
          this.showToastMessage(message, TOAST_TYPES.Success);
        }),
        catchError(() => {
          this.showToastMessage(this.errorMessage, TOAST_TYPES.Error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  handleConfirmOption(actionType: string, successMessage: string, errorMessage: string): void {
    const orderToChange = [
      {
        resourceId: this.scheduleId,
        type: DISPENSE_ACTIONS.DO_TRANSACTION,
        params: {
          actionType,
        },
      },
    ];

    this.helmsdeepService
      .actions(orderToChange)
      .pipe(
        tap(() => {
          const message = this.translateService.instant(successMessage, {
            scheduleName: this.scheduleName,
          });
          this.showToastMessage(message, TOAST_TYPES.Success);
        }),
        catchError(() => {
          const message = this.translateService.instant(errorMessage, {
            scheduleName: this.scheduleName,
          });
          this.showToastMessage(message, TOAST_TYPES.Error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  showToastMessage(message: string, type: TOAST_TYPES): void {
    this.apiErrorHandlerService.showMessage(message, type);
    this.router.navigate([`${this.nextUrl}`]);
  }

  handleConfirmOptionForVerifyOrder(): void {
    this.handleConfirmOption(
      DISPENSE_ACTIONS.DO_VERIFY,
      this.orderVerifiedSuccessfulMessage,
      this.orderErrorMessage
    );
  }

  handleConfirmOptionForLockdownOrder(): void {
    this.handleConfirmOption(
      DISPENSE_ACTIONS.DO_LOCKEDDOWN,
      this.orderLockedSuccessfulMessage,
      this.orderErrorMessage
    );
  }

  handleConfirmOptionForDispenseOrder(): void {
    this.subscription.add(
      this.helmsdeepService
        .getData('schedules', this.scheduleId, 'dispenses')
        .pipe(
          tap((scheduleNdispenses: object) => {
            const keepScheduleNdispenses = this.prepareScheduleData(scheduleNdispenses);
            const action = DISPENSE_ACTIONS.DO_DISPENSE;

            this.startDispense(keepScheduleNdispenses, action);
          }),
          catchError((error) => {
            this.apiErrorHandlerService.handleApiError(error, '');
            return of(null);
          })
        )
        .subscribe()
    );
  }

  startDispense(keepScheduleNdispenses: object, actionType: string): void {
    const orderToChange = [
      {
        resourceId: this.scheduleId,
        type: DISPENSE_ACTIONS.DO_TRANSACTION,
        params: {
          actionType,
        },
      },
    ];

    this.subscription.add(
      this.helmsdeepService
        .actions(orderToChange)
        .pipe(concatMap(() => this.finishDispense(keepScheduleNdispenses)))
        .subscribe({
          next: () => {},
          error: () => {
            const message = this.translateService.instant(this.orderErrorMessage, {
              scheduleName: this.scheduleName,
            });
            this.showToastMessage(message, TOAST_TYPES.Error);
          },
        })
    );
  }

  finishDispense(scheduleInfo: object): Observable<object> {
    const spacesIds = scheduleInfo[this.scheduleId]['spacesId'];
    const positions = scheduleInfo[this.scheduleId]['positionsNoids'];

    const orderToChange = [
      {
        resourceId: this.scheduleId,
        type: DISPENSE_ACTIONS.DO_TRANSACTION,
        params: {
          actionType: DISPENSE_ACTIONS.FINISH_DISPENSE,
        },
      },
    ];
    return this.helmsdeepService.actions(orderToChange).pipe(
      tap(() => {
        this.validatePresence(spacesIds, positions);
      })
    );
  }

  redirect(option): void {
    this.isLoading = true;
    // bind is used to set the correct context for the function in the current instance
    const confirmOptionHandlers = {
      [VIEW_CALLING.LOADING_ORDER]: this.handleConfirmOptionForLoadingOrder.bind(this),
      [VIEW_CALLING.VERIFY_ORDER]: this.handleConfirmOptionForVerifyOrder.bind(this),
      [VIEW_CALLING.DISPENSE_ORDER]: this.handleConfirmOptionForDispenseOrder.bind(this),
      [VIEW_CALLING.LOCKDOWN_ORDER]: this.handleConfirmOptionForLockdownOrder.bind(this),
      [VIEW_CALLING.REMOVE_ITEM]: this.handleConfirmOptionForReclaimOrder.bind(this),
      [VIEW_CALLING.RECLAIM_ORDER]: this.handleConfirmOptionForReclaimOrder.bind(this),
      [VIEW_CALLING.RECLAIM_ALL_ORDER]: this.handleConfirmOptionForReclaimAllOrder.bind(this),
      [VIEW_CALLING.OPEN_DOOR]: this.handleConfirmOptionForOpenDoor.bind(this),
      [VIEW_CALLING.SELECTABLE_SPACE]: this.handleConfirmOptionForSelectableSpace.bind(this),
      [VIEW_CALLING.RESET_SPACE]: this.handleConfirmOptionForResetSpace.bind(this),
      [VIEW_CALLING.DISABLE_SPACE]: this.handleConfirmOptionForSelectableSpace.bind(this),
      [VIEW_CALLING.ENABLE_SPACE]: this.handleConfirmOptionForSelectableSpace.bind(this),
    };
    if (option === 'confirm' && confirmOptionHandlers[this.viewCalling]) {
      confirmOptionHandlers[this.viewCalling]();
    } else {
      this.clearInactivityTimerAndNavigate();
    }
  }

  startReclaim(keepScheduleNdispenses: object, actionType: string, side: string): void {
    const scheduleActions = Object.entries(keepScheduleNdispenses).map(([scheduleId, data]) => {
      const scheduleActionParams: IScheduleActionParams = {
        scheduleId,
        type: DISPENSE_ACTIONS.DO_TRANSACTION,
        actionType,
        smartDoor: false,
        side,
        hmi: false,
        app: APP_ID,
      };

      return this.helmsdeepService
        .scheduleAction(scheduleActionParams)
        .pipe(tap(() => this.handleActionReclaim(actionType, side, scheduleId, data)));
    });
    forkJoin(scheduleActions).subscribe({
      next: () => {},
      error: () => {
        this.showToastMessage(this.errorMessage, TOAST_TYPES.Error);
      },
    });
  }

  handleActionReclaim(actionType: string, side: string, scheduleId: string, data: any): void {
    if (actionType === DISPENSE_ACTIONS.DO_RECLAIM) {
      this.finishReclaimAllSchedule(
        scheduleId,
        DISPENSE_ACTIONS.FINISH_RECLAIM,
        data['spacesId'],
        side,
        data['spacesOids'],
        data['positionsNoids']
      );
    } else if (actionType === DISPENSE_ACTIONS.DO_REMOVE) {
      this.validatePresence(data['spacesId'], data['positionsNoids']);
      if (side === 'REAR') {
        this.handleRemoveAction(side, data, scheduleId);
      }
    }
  }

  private handleRemoveAction(side: string, data: any, scheduleId: string): void {
    let processes = data['spacesOids'].reduce((a, v) => ({ ...a, [v]: 'RECLAIM_REAR' }), {});
    this.babelfishService.reclaimLights(scheduleId, side, processes, this.deviceSn); // compartment Reclaim Lights
    processes = data['spacesOids'].reduce((a, v) => ({ ...a, [v]: 'RECLAIMED_REAR' }), {});
    this.babelfishService.reclaimLights(
      scheduleId,
      side,
      processes,
      this.deviceSn,
      this.removeLightsTimeout
    ); // Compartment Reclaimed Lights(Absent)
  }

  validatePresence(spacesId: string[], positionsNoids: Record<string, string> = {}): void {
    if (this.validatePresenceSensor) {
      this.showToastMessageAndNavigate(positionsNoids);
    } else {
      this.unfillSpaces(spacesId, positionsNoids);
    }
  }

  unfillSpaces(spacesId: string[], positionsNoids: Record<string, string> = {}): void {
    this.helmsdeepService
      .unfillSpaces(spacesId)
      .pipe(
        retry({
          count: 1,
          delay: (error) => {
            if (error?.status === 500) {
              console.log(`retrying unfill transaction for ${spacesId}`);
              return timer(UNFILL_RETRY_DELAY_MS);
            }
            return throwError(() => error);
          },
        }),
        tap({
          next: () => {
            this.showToastMessageAndNavigate(positionsNoids);
          },
        }),
        catchError((error) => {
          console.log('error', error);
          this.showToastMessage(error, TOAST_TYPES.Error);
          return of(null);
        })
      )
      .subscribe();
  }

  finishReclaimAllSchedule(
    scheduleId: string,
    actionType: string,
    spacesId: string[],
    side: string,
    oidsProcess: string[],
    positionsNoids: Record<string, string>
  ): void {
    const scheduleActionParams: IScheduleActionParams = {
      scheduleId,
      type: DISPENSE_ACTIONS.DO_TRANSACTION,
      actionType,
      smartDoor: false,
      side,
      hmi: false,
      app: APP_ID,
    };

    this.helmsdeepService
      .scheduleAction(scheduleActionParams)
      .pipe(
        tap({
          next: () => {
            if (side === SIDES.REAR) {
              this.lightUpSpaceInReclaim(side, scheduleId, oidsProcess);
            }
            this.validatePresence(spacesId, positionsNoids);
          },
          error: (error) => {
            this.apiErrorHandlerService.handleApiError(error, '');
          },
        })
      )
      .subscribe();
  }

  showToastMessageAndNavigate(positionsNoids: Record<string, string>): void {
    const successMessageKey =
      this.viewCalling === VIEW_CALLING.DISPENSE_ORDER
        ? this.orderDispensedSuccessfulMessage
        : this.orderReclaimedSuccessfulMessage;

    const message = this.translateService.instant(successMessageKey, {
      scheduleName: this.scheduleName,
    });

    this.apiErrorHandlerService.showMessage(message, TOAST_TYPES.Success);

    this.router.navigate([
      `${this.nextUrl}`,
      {
        positionsNoids: JSON.stringify(positionsNoids),
        scheduleId: this.scheduleId,
      },
    ]);
  }

  lightUpSpaceInReclaim(side: string, scheduleId: string, oidsProcess: any) {
    const processes = oidsProcess.reduce((a, v) => ({ ...a, [v]: `RECLAIMED_${side}` }), {});
    this.babelfishService.reclaimLights(
      scheduleId,
      side,
      processes,
      this.deviceSn,
      this.removeLightsTimeout
    );
  }

  inactivityTimeout(timeout: number): void {
    this.inactivityTimer = setTimeout(() => {
      this.router.navigate([`${this.nextUrl}`]);
    }, timeout);
  }

  ngOnDestroy() {
    clearTimeout(this.inactivityTimer);
    if (this.removeLightsTimeoutRef) {
      clearTimeout(this.removeLightsTimeoutRef);
    }
    this.subscription.unsubscribe();
  }
}
